import { useState } from "react";
import classNames from "classnames";
import { Modal } from "../../../components/molecules";
import { MODALS } from "../../../components/organisms/AppModals/modals.constants";
import { useDispatch } from "react-redux";
import { actions as modalActions } from "../../../components/organisms/Modal/modal.reducers";
import {
  menuEditIcon,
  hidePasswordIcon,
  showPasswordIcon,
} from "../../../assets/icons";
import { Field, Formik } from "formik";
import * as Yup from "yup";
import { EMPTY_STRING, REGEX, VALIDATION_MESSAGES } from "../../../constants";
import isEmpty from "lodash/isEmpty";
import Spinner, { SPINNER_NAMES } from "../../../components/organisms/Spinner";
import { validateSmtpConnection } from "..";
import { getEncryptedSecretKey, getDecryptedSecretKey } from "../../../helpers";

const { setSelectedModal } = modalActions;
const { SMTP_CONNECTION_MODAL } = MODALS;
const { VALIDATE_SMTP_CONNECTION } = SPINNER_NAMES;
const {
  NAME_REQUIRED,
  TYPE_REQUIRED,
  HOST_REQUIRED,
  PORT_DATA_TYPE,
  PORT_REQUIRED,
  USER_REQUIRED,
  PASSWORD_REQUIRED,
} = VALIDATION_MESSAGES;

const SMTP = "SMTP";
const DEFAULT_VALUES = {
  name: EMPTY_STRING,
  type: SMTP,
  host: EMPTY_STRING,
  port: EMPTY_STRING,
  userName: EMPTY_STRING,
  password: EMPTY_STRING,
  secure: false,
};

const encryptDecryptKey = (value, isEncrypted) =>
  isEncrypted ? getDecryptedSecretKey(value) : getEncryptedSecretKey(value);

const formatValues = (values, isEncrypted) => ({
  ...values,
  userName: encryptDecryptKey(values.userName, isEncrypted),
  password: encryptDecryptKey(values.password, isEncrypted),
});

const SmtpConnection = ({ values, handleSubmit }) => {
  const dispatch = useDispatch();
  const [initialValues, setInitialValues] = useState(
    (values && formatValues(values, true)) || DEFAULT_VALUES
  );
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [isValidConnection, setIsValidConnection] = useState(false);
  const [showPassword, setShouldShowPassword] = useState(false);
  const [shouldShowValidationMessage, setShouldShowValidationMessage] =
    useState(false);

  const handleClose = () => {
    dispatch(setSelectedModal(null));
    setShouldShowModal(false);
    setShouldShowValidationMessage(false);
  };

  const handleOnSubmit = (values) => {
    handleSubmit(formatValues(values, false));
    setInitialValues(values);
    setIsValidConnection(false);
    handleClose();
  };

  const handleShouldShowModal = () => {
    dispatch(setSelectedModal(SMTP_CONNECTION_MODAL));
    setShouldShowModal(true);
  };

  const validateConnection = (values) => {
    dispatch(validateSmtpConnection(formatValues(values, false))).then(
      ({ payload }) => {
        setIsValidConnection(payload?.validSmtpConnection);
        setShouldShowValidationMessage(true);
      }
    );
  };

  const handleOnChange = (e, handleChange) => {
    handleChange(e);
    setShouldShowValidationMessage(false);
    setIsValidConnection(false);
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(NAME_REQUIRED),
    type: Yup.string().required(TYPE_REQUIRED),
    host: Yup.string().required(HOST_REQUIRED),
    port: Yup.string()
      .matches(REGEX.NUMERIC, PORT_DATA_TYPE)
      .required(PORT_REQUIRED),
    userName: Yup.string().required(USER_REQUIRED),
    password: Yup.string().required(PASSWORD_REQUIRED),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ values, touched, errors, handleChange, handleReset }) => {
        const footer = (
          <div className="p-4 border-t border-gray-700 text-right">
            <button
              type="button"
              className="btn btn-primary mr-1"
              disabled={!isValidConnection}
              onClick={() => handleOnSubmit(values)}
            >
              {initialValues.name ? "Update" : "Create"}
            </button>
            <button
              type="button"
              className="btn btn-primary-light"
              onClick={() => {
                handleReset();
                handleClose();
              }}
            >
              Cancel
            </button>
          </div>
        );

        return (
          <>
            <div className="flex items-center justify-between bg-gray-900 p-2">
              <label className="text-sm">
                {initialValues.name || "Create External Connection"}
              </label>
              <button type="button" onClick={handleShouldShowModal}>
                <img src={menuEditIcon} width="20" alt="menuEditIcon" />
              </button>
            </div>

            {shouldShowModal && (
              <Modal
                title={`${
                  initialValues.name ? "Update" : "Create"
                } External Connection`}
                position="top"
                footer={footer}
                handleReset={handleReset}
                handleClose={handleClose}
              >
                <div className="flex flex-col gap-2 p-4 text-sm">
                  <div className="form-group">
                    <label>Name</label>
                    <Field
                      type="text"
                      name="name"
                      value={values.name}
                      className={classNames("form-control mt-0.5 border-2", {
                        "border-red-600": touched.name && errors.name,
                      })}
                      placeholder="Name"
                    />
                    {touched.name && errors.name && (
                      <div className="text-red-500 mt-0.5">{errors.name}</div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <Field
                      as="select"
                      className="form-control mt-0.5"
                      name="type"
                      value={values.type}
                    >
                      <option value={SMTP}>SMTP (email) Server</option>
                    </Field>
                  </div>
                  <div className="font-bold text-base mt-1">
                    SMTP (email) server connection parameters
                  </div>
                  <div className="form-group">
                    <label>Host</label>
                    <Field
                      type="text"
                      name="host"
                      value={values.host}
                      className={classNames("form-control mt-0.5 border-2", {
                        "border-red-600": touched.host && errors.host,
                      })}
                      onChange={(e) => handleOnChange(e, handleChange)}
                      placeholder="Host"
                    />
                    {touched.host && errors.host && (
                      <div className=" text-red-500 mt-0.5">{errors.host}</div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Port</label>
                    <Field
                      type="text"
                      name="port"
                      value={values.port}
                      className={classNames("form-control mt-0.5 border-2", {
                        "border-red-600": touched.port && errors.port,
                      })}
                      onChange={(e) => handleOnChange(e, handleChange)}
                      placeholder="Port"
                    />
                    {touched.port && errors.port && (
                      <div className="text-red-500 mt-0.5">{errors.port}</div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Username</label>
                    <Field
                      type="text"
                      name="userName"
                      value={values.userName}
                      className={classNames("form-control mt-0.5 border-2", {
                        "border-red-600": touched.userName && errors.userName,
                      })}
                      onChange={(e) => handleOnChange(e, handleChange)}
                      placeholder="User"
                    />
                    {touched.userName && errors.userName && (
                      <div className="text-red-500 mt-0.5">
                        {errors.userName}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Password</label>
                    <div className="flex flex-between items-center bg-[#E8F0FE] rounded-md">
                      <Field
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={values.password}
                        className={classNames("form-control border-2", {
                          "border-red-600": touched.password && errors.password,
                        })}
                        onChange={(e) => handleOnChange(e, handleChange)}
                        placeholder="Password"
                      />
                      <img
                        className="w-8 h-4 cursor-pointer"
                        src={showPassword ? showPasswordIcon : hidePasswordIcon}
                        alt="Password"
                        onClick={() => setShouldShowPassword(!showPassword)}
                      />
                    </div>
                    {touched.password && errors.password && (
                      <div className="text-red-500 mt-0.5">
                        {errors.password}
                      </div>
                    )}
                  </div>
                  <div className="form-group flex items-center gap-2">
                    <Field
                      type="checkbox"
                      name="secure"
                      checked={values.secure}
                      onChange={(e) => handleOnChange(e, handleChange)}
                      className="text-indigo-600 rounded focus:ring-0 focus:outline-indigo-300 cursor-pointer"
                    />
                    <label>Use TLS when connecting to server</label>
                  </div>
                  <div className="form-group flex items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-primary-light w-fit mt-1"
                      disabled={!isEmpty(errors) || isValidConnection}
                      onClick={() => validateConnection(values)}
                    >
                      <Spinner name={VALIDATE_SMTP_CONNECTION} persistSize>
                        Test Connection
                      </Spinner>
                    </button>
                    {shouldShowValidationMessage &&
                      (isValidConnection ? (
                        <span className="text-green-400">Success</span>
                      ) : (
                        <span className="text-red-400">Error</span>
                      ))}
                  </div>
                </div>
              </Modal>
            )}
          </>
        );
      }}
    </Formik>
  );
};
export default SmtpConnection;
